import { useState } from "react";
import { Button } from "@material-ui/core";
import moment from "moment";
import { CONSTANTS } from "../../constants/app-constant";
import { practiceSuggestions, PRACTICE_SUGGESTION_POLICY } from "./student-practice-options";

const quizWord = (count) => (count === 1 ? "quiz" : "quizzes");
// Both surfaces can be on screen at once (Do together card + Assign dialog).
let headingSeq = 0;

// One suggestion block for both tutor practice surfaces (Assign a quiz and
// Do together). It never changes a filter by itself: topics or missed-question
// review apply only when the tutor clicks. Scores are whole-quiz scores, so the
// copy says so instead of presenting them as per-topic accuracy.
export default function PracticeSuggestions({ history, exam, catalog, questionSelection, onUseTopics, onReviewMissed, now }) {
  const [headingId] = useState(() => `practice-suggestions-title-${++headingSeq}`);
  const summary = practiceSuggestions(history, exam, catalog, now);
  const { windowDays, maxTopicsPerQuiz, minQuizzes, minQuestions } = PRACTICE_SUGGESTION_POLICY;
  const examName = (CONSTANTS.examTypeList || []).find((option) => option.value === exam)?.name || exam;
  // Quizzes that also covered another exam are left out, so name the scope.
  const examOnly = `${examName}-only`;
  const recentQuizzes = `${summary.quizzes} independent ${examOnly} ${quizWord(summary.quizzes)}`;
  const focusRange = `1–${maxTopicsPerQuiz} topics`;
  const missedSelected = questionSelection === "INCORRECT";
  const lastSeen = summary.lastIndependentAt ? ` (most recent ${moment(summary.lastIndependentAt).format("ll")})` : "";

  return (
    <section className="practice-suggestions" aria-labelledby={headingId} data-testid="practice-suggestions">
      <h3 className="ps-title" id={headingId}>Practice suggestions</h3>
      {summary.topics.length > 0 ? (
        <>
          <p className="ps-text">Focused-quiz scores, last {windowDays} days, lowest first:</p>
          <ul className="ps-topics">
            {summary.topics.map((topic) => (
              <li key={topic.name}>
                {`${topic.name} — ${topic.accuracy}% on ${topic.quizzes} ${quizWord(topic.quizzes)} that included it `
                  + `(${topic.questions} questions in those quizzes)`}
              </li>
            ))}
          </ul>
          <Button size="small" data-testid="practice-use-topics"
            onClick={() => onUseTopics(summary.topics.map((topic) => topic.name))}>
            Use these topics
          </Button>
          <p className="ps-note">
            {`From ${recentQuizzes} (${summary.focusedQuizzes} aimed at ${focusRange}). `}
            Scores and question counts are for whole quizzes, so a quiz aimed at several topics counts fully for
            each. Tutor-mode, done-together and multi-exam quizzes are left out.
          </p>
        </>
      ) : (
        <>
          <p className="ps-text">
            {summary.quizzes > 0
              ? `No topic suggestion yet: ${recentQuizzes} in the last ${windowDays} days, ${summary.focusedQuizzes} aimed at ${focusRange}. A topic needs ${minQuizzes} such quizzes and ${minQuestions}+ questions in total.`
              : `No topic suggestion yet: no independent ${examOnly} quizzes in the last ${windowDays} days${lastSeen}.`}
          </p>
          <div className="ps-actions">
            {summary.hasMissedQuestions && (
              // Pressed, never disabled: disabling would drop keyboard focus out of the picker or dialog.
              <Button size="small" data-testid="practice-review-missed" aria-pressed={missedSelected}
                onClick={() => { if (!missedSelected) onReviewMissed(); }}>
                Review missed questions{missedSelected && <span aria-hidden="true">&nbsp;✓</span>}
              </Button>
            )}
            <span className="ps-note">Or choose topics yourself.</span>
          </div>
        </>
      )}
    </section>
  );
}
